import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LearningSessionsApi, UserDictionaryApi } from 'src/api';
import { isRomanizationLabel } from 'src/api/settings/learningToggles';
import { Learnable, SessionType } from 'src/api/learningSessions/types';
import {
  getAudio,
  getPresentationScreenTemplate,
  getSingleValueNullable,
  selectVideoSrc,
} from 'src/apps/LearningSession/Common/learnableCards/utils';
import { makeLearnablesWithProgress } from 'src/apps/LearningSession/hooks/utils';

const PREVIEW_LIMIT = 5;
const GROWTH_PAGE_SIZE = 50;

export interface PreviewLearnable {
  id: number;
  target: string;
  gloss: string;
  phonetic: string | null;
  growthLevel: number;
  videoSrc: string | null;
  audioSrc: string | null;
}

export interface WordlistLocalsPreviewState {
  learnables: PreviewLearnable[];
  isLoading: boolean;
}

const getPresentationVideo = (learnable: Learnable): string | null => {
  try {
    const { video } = getPresentationScreenTemplate(learnable);
    if (!video) return null;
    return selectVideoSrc(video);
  } catch {
    return null;
  }
};

const getPresentationAudio = (learnable: Learnable): string | null => {
  try {
    return getAudio(learnable);
  } catch {
    return null;
  }
};

const getPhonetic = (learnable: Learnable): string | null => {
  try {
    const { visibleInfo, hiddenInfo } = getPresentationScreenTemplate(learnable);
    const romanization = [...visibleInfo, ...hiddenInfo].find(info =>
      isRomanizationLabel(info.label),
    );
    return romanization ? getSingleValueNullable(romanization) : null;
  } catch {
    return null;
  }
};

/**
 * Fetches real user growth levels for the preview learnable IDs.
 *
 * The learn-session information endpoint often returns empty/zero progress for
 * upcoming items (especially after Aprender sessions). My Words / user-dictionary
 * `STILL_LEARNING` is the reliable source for partial growth.
 */
export const fetchGrowthLevelsForLearnables = async (
  languagePairId: number,
  learnables: Pick<Learnable, 'id' | 'learningElement'>[],
): Promise<Map<string, number>> => {
  const neededIds = new Set(learnables.map(l => String(l.id)));
  const neededTargets = new Map(
    learnables.map(l => [l.learningElement.trim().toLowerCase(), String(l.id)]),
  );
  const growthById = new Map<string, number>();

  if (neededIds.size === 0) return growthById;

  let nextPageToken: string | null | undefined;

  do {
    const page = await UserDictionaryApi.getAllUserLearnables(languagePairId, {
      learning_state: 'STILL_LEARNING',
      ordering: 'LAST_TOUCHED',
      ordering_direction: 'DESCENDING',
      page_size: GROWTH_PAGE_SIZE,
      ...(nextPageToken ? { next_page_token: nextPageToken } : {}),
    });

    for (const entry of page.learnables) {
      const idKey = String(entry.learnableIdKey);
      if (neededIds.has(idKey)) {
        growthById.set(idKey, entry.growthLevel);
        neededIds.delete(idKey);
        continue;
      }

      // Fallback: match by target text when ID spaces diverge.
      const targetKey = entry.targetValue.trim().toLowerCase();
      const matchedId = neededTargets.get(targetKey);
      if (matchedId && neededIds.has(matchedId)) {
        growthById.set(matchedId, entry.growthLevel);
        neededIds.delete(matchedId);
      }
    }

    nextPageToken = page.nextPageToken;
  } while (nextPageToken && neededIds.size > 0);

  return growthById;
};

const toPreviewLearnable = (learnable: Learnable, growthLevel: number): PreviewLearnable => ({
  id: learnable.id,
  target: learnable.learningElement,
  gloss: learnable.definitionElement,
  phonetic: getPhonetic(learnable),
  growthLevel,
  videoSrc: getPresentationVideo(learnable),
  audioSrc: getPresentationAudio(learnable),
});

/**
 * Fetches the active wordlist's upcoming learn-session learnables and returns
 * the first few for use as a preview on the wordlist card. Learnables without
 * a presentation video are included; their `videoSrc` is null and `audioSrc`
 * may contain presentation audio when available.
 *
 * Growth levels are taken from user-dictionary `STILL_LEARNING` (source of
 * truth after sessions), with the session `progress` array as a fallback.
 *
 * Uses the existing `active_wordlist_for_language_pair` session-information
 * endpoint, which is read-only on the backend (no session is started, no user
 * state mutated), so it is safe to call eagerly when the card is shown.
 */
export const useWordlistLocalsPreview = ({
  languagePairId,
  enabled,
}: {
  languagePairId?: number;
  enabled: boolean;
}): WordlistLocalsPreviewState => {
  const { data, isPending } = useQuery({
    queryKey: ['wordlistLocalsPreview', languagePairId],
    queryFn: async () => {
      const session = await LearningSessionsApi.startLearningSession(SessionType.LEARN, {
        sessionSourceType: 'active_wordlist_for_language_pair',
        sessionSourceId: languagePairId as number,
      });

      const previewLearnables = session.learnables.slice(0, PREVIEW_LIMIT);
      const withSessionProgress = makeLearnablesWithProgress(
        previewLearnables,
        session.progress ?? [],
      );
      const dictionaryGrowth = await fetchGrowthLevelsForLearnables(
        languagePairId as number,
        previewLearnables,
      );

      return withSessionProgress.map(({ learnable, progress }) => {
        const fromDictionary = dictionaryGrowth.get(String(learnable.id));
        const fromSession = progress?.growthLevel;
        const growthLevel = fromDictionary ?? fromSession ?? 0;
        return toPreviewLearnable(learnable, growthLevel);
      });
    },
    enabled: enabled && !!languagePairId,
    // Always refetch when returning to the dashboard so growth levels update
    // after a learn session.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const learnables = useMemo(() => data ?? [], [data]);

  return {
    learnables,
    isLoading: enabled && !!languagePairId && isPending,
  };
};
